/** The three collection patterns, and the task-metadata reads behind filters.
 *
 *  A grid (habits), a list (reading log, streams, projects, contexts) and a
 *  month (gratitude) cover every collection the product has, which is why adding
 *  one costs no new design.
 *
 *  Reading-log mutations are revision-guarded — update and delete both require
 *  `If-Match` — while a gratitude entry is a whole-day upsert and is not.
 */

import { ApiError, request, type Session } from './client';
import { newIdempotencyKey } from './ids';
import type {
  WireCommonplaceBook,
  WireCommonplaceEntry,
  WireEntry,
  WireGratitudeEntry,
  WireReadingLogItem,
  WireTaskMetadata,
} from './wire';

/* ---------------------------------------------------------------- reading log */

export async function readingLog(session: Session): Promise<WireReadingLogItem[]> {
  return (
    await request<{ items: WireReadingLogItem[] }>(session, '/v1/collections/reading-log?limit=100')
  ).body.items;
}

export async function addBook(
  session: Session,
  book: { title: string; author?: string; genre?: string; rating: number; start_date?: string },
): Promise<void> {
  await request(session, '/v1/collections/reading-log', {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
    body: book,
  });
}

/** Reading status is **derived**, not stored.
 *
 *  `reading_log_items` has `start_date`, `end_date` and `rating` and no status
 *  column, so a book is *reading* while it has no end date and *finished* once
 *  it has one. There is no way to record that a book was abandoned, nor the page
 *  it was stopped at — the design's `ABANDONED · 1` and `stopped p.140` both
 *  need server fields that do not exist. Named in the UI rather than faked.
 */
export function readingStatus(item: WireReadingLogItem): 'reading' | 'finished' {
  return item.end_date ? 'finished' : 'reading';
}

/** Finish a book by dating it. Revision-guarded like any other update. */
export async function finishBook(
  session: Session,
  id: string,
  endDate: string,
): Promise<'ok' | 'conflict'> {
  return patchBook(session, id, { end_date: endDate });
}

/** Put a finished book back into reading by clearing its end date. `null` is
 *  meaningful here: the request type is `Option<Option<String>>`, so an explicit
 *  null clears the field where omitting it would leave it alone. */
export async function resumeBook(session: Session, id: string): Promise<'ok' | 'conflict'> {
  return patchBook(session, id, { end_date: null });
}

async function patchBook(
  session: Session,
  id: string,
  body: Record<string, unknown>,
): Promise<'ok' | 'conflict'> {
  const { etag } = await request<WireReadingLogItem>(
    session,
    `/v1/collections/reading-log/${id}`,
  );
  try {
    await request(session, `/v1/collections/reading-log/${id}`, {
      method: 'PATCH',
      etag,
      idempotencyKey: newIdempotencyKey(),
      body,
    });
    return 'ok';
  } catch (error) {
    if (error instanceof ApiError && error.isRevisionConflict) return 'conflict';
    throw error;
  }
}

/** Rate a book. Reads the current ETag first, because update is
 *  revision-guarded and a stale validator is a 412, not a silent overwrite. */
export async function rateBook(
  session: Session,
  id: string,
  rating: number,
): Promise<'ok' | 'conflict'> {
  return patchBook(session, id, { rating });
}

export async function removeBook(session: Session, id: string): Promise<void> {
  const { etag } = await request<WireReadingLogItem>(
    session,
    `/v1/collections/reading-log/${id}`,
  );
  await request(session, `/v1/collections/reading-log/${id}`, {
    method: 'DELETE',
    etag,
    idempotencyKey: newIdempotencyKey(),
  });
}

/* ------------------------------------------------------------------ gratitude */

export async function gratitudeMonth(
  session: Session,
  month: string,
): Promise<WireGratitudeEntry[]> {
  return (
    await request<{ items: WireGratitudeEntry[] }>(
      session,
      `/v1/collections/gratitude-log?month=${month}`,
    )
  ).body.items;
}

/** One entry per day, replaced wholesale. Not revision-guarded: the day is the
 *  key and there is only ever one entry on it. */
export async function putGratitude(
  session: Session,
  date: string,
  content: string,
): Promise<void> {
  await request(session, `/v1/collections/gratitude-log/${date}`, {
    method: 'PUT',
    idempotencyKey: newIdempotencyKey(),
    body: { content },
  });
}

/* -------------------------------------------------------------- task metadata */

export type MetadataKind = 'streams' | 'projects' | 'contexts';

export async function metadata(
  session: Session,
  kind: MetadataKind,
): Promise<WireTaskMetadata[]> {
  return (await request<{ items: WireTaskMetadata[] }>(session, `/v1/${kind}?limit=100`)).body
    .items;
}

/** Tasks carrying one piece of metadata. The query parameter is singular and
 *  differs from the collection name, which is easy to get wrong.
 *
 *  Reports whether the page was cut short. R13's rule is that hidden content is
 *  never silent, and a state filter counted from a truncated page would quietly
 *  understate every total. */
export async function tasksBy(
  session: Session,
  kind: MetadataKind,
  name: string,
): Promise<{ items: WireEntry[]; truncated: boolean }> {
  const param = kind === 'streams' ? 'stream' : kind === 'projects' ? 'project' : 'context';
  const page = (
    await request<{ items: WireEntry[]; next_cursor: string | null }>(
      session,
      // 100 is MAX_PAGE_SIZE. `/v1/entries` *rejects* a larger limit with a 422
      // rather than clamping it, unlike the metadata endpoints — asking for 200
      // made every filtered list fail outright.
      `/v1/entries?kind=task&${param}=${encodeURIComponent(name)}&limit=100`,
    )
  ).body;
  return { items: page.items, truncated: Boolean(page.next_cursor) };
}

/* ------------------------------------------------------- commonplace books */

export async function commonplaceBooks(session: Session): Promise<WireCommonplaceBook[]> {
  return (
    await request<{ items: WireCommonplaceBook[] }>(
      session,
      '/v1/collections/commonplace-books?limit=100',
    )
  ).body.items;
}

export async function commonplaceEntries(
  session: Session,
  bookId: string,
): Promise<WireCommonplaceEntry[]> {
  return (
    await request<{ items: WireCommonplaceEntry[] }>(
      session,
      `/v1/collections/commonplace-books/${bookId}/entries?limit=100`,
    )
  ).body.items;
}

/** The `If-Match` a commonplace entry expects.
 *
 *  Derived rather than read: there is no `GET .../entries/{id}`, so the only
 *  source for it is the list response — which carries the `id` and `revision`
 *  this is built from. That couples the client to the service's format, so
 *  `src/lib.rs` pins it with a test that names this function.
 */
export function commonplaceEntryEtag(entry: { id: string; revision: number }): string {
  return `"commonplace-entry:${entry.id}:${entry.revision}"`;
}

/** Change what a kept entry says.
 *
 *  `source` is deliberately absent: `UpdateCommonplaceEntryRequest` does not
 *  accept it, so a citation is fixed once kept. Passing it would be silently
 *  dropped, which is worse than not offering it.
 */
export async function editCommonplaceEntry(
  session: Session,
  bookId: string,
  entry: { id: string; revision: number },
  changes: { body?: string; title?: string; entry_type?: string; tags?: string[] },
): Promise<void> {
  await request(session, `/v1/collections/commonplace-books/${bookId}/entries/${entry.id}`, {
    method: 'PATCH',
    etag: commonplaceEntryEtag(entry),
    idempotencyKey: newIdempotencyKey(),
    body: changes,
  });
}

export async function addCommonplaceEntry(
  session: Session,
  bookId: string,
  entry: {
    entry_type: string;
    title: string;
    body: string;
    source?: string;
    tags?: string[];
  },
): Promise<void> {
  await request(session, `/v1/collections/commonplace-books/${bookId}/entries`, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
    body: {
      entry_type: entry.entry_type,
      title: entry.title,
      body: entry.body,
      source: entry.source ?? '',
      // Sent as an array; the service stores and returns it as `tags_json`.
      tags: entry.tags ?? [],
      // Typed by a person on this device. `llm`, `import` and `api` exist for
      // other producers and this client is never any of them.
      source_type: 'typed',
    },
  });
}

export async function addCommonplaceBook(
  session: Session,
  title: string,
  description = '',
): Promise<void> {
  await request(session, '/v1/collections/commonplace-books', {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
    // The description is the subtitle the book index shows under the name —
    // `things worth keeping`. It was hardcoded empty, so a book made from here
    // could never have one.
    body: { title, description },
  });
}
